import type { MetricRecord } from "@/metrics/runtime"

import {
  experimentValueToOutput,
  humanReviewToAnnotation,
} from "../../utils/computeExperimentAgreement"
import type {
  CriteriaViewMode,
  ExtendedGroupedSimulationData,
} from "../components/SimulationReviewRow"
import { hasCompositeAgreementConflict } from "./compositeAgreementConflict"

/**
 * Whether the counted human label disagrees with the AI review or the most
 * recent experiment for this conversation. Drives the red row highlight.
 */
export const hasRowConflict = (
  data: ExtendedGroupedSimulationData,
  metric: MetricRecord | null,
  criteriaViewMode: CriteriaViewMode,
): boolean => {
  if (data.humanReviews.length === 0 || !metric) {
    return false
  }

  const primaryHumanReview =
    data.humanReviews.find((r) => r.isCountedAsPrimary) || data.humanReviews[0]

  const isComposite = metric.metric_manager === "manager_composite_evaluation"

  if (isComposite) {
    if (data.rawMetricOutput) {
      const hasConflict = hasCompositeAgreementConflict(
        {
          float_value: data.rawMetricOutput.float_value,
          set_value: data.rawMetricOutput.set_value ?? null,
          result: data.rawMetricOutput.result ?? null,
          result_json: data.rawMetricOutput.result_json ?? null,
        },
        {
          float_value: primaryHumanReview.float_value,
          set_value: primaryHumanReview.set_value,
          json_value: primaryHumanReview.json_value,
        },
        metric.metric_metadata,
        criteriaViewMode,
      )
      if (hasConflict) {
        return true
      }
    }
    if (
      data.mostRecentExperiment.value &&
      data.mostRecentExperiment.value !== "ERROR"
    ) {
      const hasConflict = hasCompositeAgreementConflict(
        {
          float_value: data.mostRecentExperiment.rawFloatValue ?? null,
          set_value:
            data.mostRecentExperiment.criteriaResults?.map((c) => c.result) ??
            null,
        },
        {
          float_value: primaryHumanReview.float_value,
          set_value: primaryHumanReview.set_value,
          json_value: primaryHumanReview.json_value,
        },
        metric.metric_metadata,
        criteriaViewMode,
      )
      if (hasConflict) {
        return true
      }
    }
    return false
  }

  const hasHumanValue =
    primaryHumanReview.float_value !== null ||
    primaryHumanReview.string_value !== null ||
    (primaryHumanReview.set_value !== null &&
      primaryHumanReview.set_value.length > 0)
  if (!hasHumanValue) {
    return false
  }

  if (data.rawMetricOutput) {
    const annotation = humanReviewToAnnotation(
      primaryHumanReview,
      data.simulationOutputId,
      metric.id,
      primaryHumanReview.isCountedAsPrimary ? "PRIMARY" : "STANDARD",
    )

    const agrees = metric.annotationMatchesMetricOutput(
      annotation,
      data.rawMetricOutput,
    )

    if (!agrees) {
      return true // Conflict detected
    }
  }

  if (
    data.mostRecentExperiment.value &&
    data.mostRecentExperiment.value !== "ERROR"
  ) {
    const outputType =
      data.rawMetricOutput?.output_type || metric.output_type || "string"

    const experimentOutput = experimentValueToOutput(
      data.mostRecentExperiment.value,
      outputType,
    )
    if (data.mostRecentExperiment.rawFloatValue != null) {
      experimentOutput.float_value = data.mostRecentExperiment.rawFloatValue
    }
    const annotation = humanReviewToAnnotation(
      primaryHumanReview,
      data.simulationOutputId,
      metric.id,
      primaryHumanReview.isCountedAsPrimary ? "PRIMARY" : "STANDARD",
    )

    if (!metric.annotationMatchesMetricOutput(annotation, experimentOutput)) {
      return true // Conflict detected
    }
  }

  return false
}
